/**
 * Edit an organization.
 */

import { Form, redirect, useActionData, useLoaderData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { updateOrganizationInput, type Organization } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { OrganizationFields, readOrganizationForm } from '../components/organization-form'
import { Button, PageHeader } from '../ui'

export async function loader({ params }: LoaderFunctionArgs): Promise<Organization> {
  const id = params.organizationId
  if (!id) throw new Response('Missing organization id', { status: 400 })
  return api.organizations.get(id)
}

export async function action({
  request,
  params,
}: ActionFunctionArgs): Promise<FormResult | Response> {
  const id = params.organizationId
  if (!id) throw new Response('Missing organization id', { status: 400 })

  const parsed = updateOrganizationInput.safeParse(readOrganizationForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    await api.organizations.update(id, parsed.data)
    return redirect(`/crm/organizations/${id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function EditOrganization() {
  const organization = useLoaderData() as Organization
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  return (
    <>
      <PageHeader title={`Edit ${organization.name}`} />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <OrganizationFields organization={organization} issues={result?.issues ?? {}} />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <a className="ax-btn" href={`/crm/organizations/${organization.id}`}>
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
